import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AutoGrowTextInput } from '../../components/AutoGrowTextInput';
import { CalendarDatePicker } from '../../components/CalendarDatePicker';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ProjectMemberPicker } from '../../components/ProjectMemberPicker';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createProjectSubtask,
  deleteProjectSubtask,
  deleteProjectTask,
  getProjectTask,
  moveProjectTask,
  reorderProjectSubtasks,
  updateProjectSubtask,
  updateProjectTask,
} from '../../services/projectService';
import type {
  ProjectColumn,
  ProjectMember,
  ProjectSubtask,
  ProjectTask,
} from '../../types/project';
import { formatCalendarDate, todayBusinessCalendarDate } from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  formatDurationCompact,
  formatDurationInput,
  isSubtaskList,
  newSubtaskPoints,
  planTaskMove,
  splitSubtaskPoints,
  SUBTASK_NAME_MAX_LENGTH,
  validateCreateTaskForm,
  type TaskFormFieldErrors,
} from '../../utils/projectTaskForm';
import { finishEditingProps } from '../../utils/textInputEvents';
import {
  FOCUS_TIMER_PRESETS,
  type FocusTimerMode,
} from '../../utils/taskFocusTimer';
import type { TaskFocusTimerView } from '../../hooks/useTaskFocusTimer';
import { CompleteTaskModal } from './CompleteTaskModal';
import { TaskTimeLogView } from './TaskTimeLogView';
import { runOperation } from '../../utils/operationFeedback';

type Props = {
  projectId: number;
  taskId: number;
  members: ProjectMember[];
  columns: ProjectColumn[];
  canEdit: boolean;
  initialTask?: ProjectTask | null;
  onClose: () => void;
  onTaskChanged?: () => void;
  /** Called after the task itself is deleted; the panel should be closed. */
  onDeleted?: (taskId: number) => void;
  activeTimer: TaskFocusTimerView | null;
  onStartTimer: (mode: FocusTimerMode) => void;
  onStopTimer: () => void;
  /** Changes when a focus session is logged, so time spent is reloaded. */
  timeLogVersion?: number;
};

type Editor = 'due' | 'members' | 'move' | 'timer' | null;

/** A subtask row: a DOM node on web, a native view elsewhere. */
type SubtaskRowNode = {
  getBoundingClientRect?: () => { top: number; height: number };
  measureInWindow?: (
    callback: (x: number, y: number, width: number, height: number) => void,
  ) => void;
};

/** The browser window's listener API (this project has no DOM typings). */
type WebPointerTarget = {
  addEventListener: (type: string, listener: (event: { clientY: number }) => void) => void;
  removeEventListener: (type: string, listener: (event: { clientY: number }) => void) => void;
};

const CARD_MIN_WIDTH = 280;
const CARD_MAX_WIDTH = 720;
const RAIL_WIDTH = 72;

export function TaskDetailsPanel({
  projectId,
  taskId,
  members,
  columns,
  canEdit,
  initialTask,
  onClose,
  onTaskChanged,
  onDeleted,
  activeTimer,
  onStartTimer,
  onStopTimer,
  timeLogVersion = 0,
}: Props) {
  const palette = useAppColors();
  const { width: windowWidth } = useWindowDimensions();
  // The member field sits beside the subtask text; narrower on phones.
  const memberFieldWidth = windowWidth < 600 ? 120 : 180;
  // Width the panel actually has; until it is measured, assume the window.
  const [frameWidth, setFrameWidth] = useState(0);
  // The card fills the space beside the rail; below its minimum the panel
  // scrolls horizontally instead of cutting content off.
  const cardWidth = Math.min(
    CARD_MAX_WIDTH,
    Math.max(CARD_MIN_WIDTH, (frameWidth || windowWidth) - RAIL_WIDTH - spacing.md),
  );
  const [task, setTask] = useState<ProjectTask | null>(initialTask ?? null);
  const [taskName, setTaskName] = useState(initialTask?.taskName ?? '');
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<number[]>(
    initialTask?.assignees.map(item => item.employeeId) ?? [],
  );
  const [dueDate, setDueDate] = useState(initialTask?.dueDate ?? '');
  const [timeEstimate, setTimeEstimate] = useState(
    formatDurationInput(initialTask?.timeEstimateMinutes),
  );
  const [timeSpent, setTimeSpent] = useState(
    formatDurationInput(initialTask?.timeSpentMinutes) || '00:00',
  );
  const [description, setDescription] = useState(initialTask?.description ?? '');
  const [newSubtaskName, setNewSubtaskName] = useState('');
  const [newSubtaskMemberIds, setNewSubtaskMemberIds] = useState<number[]>([]);
  const [subtaskDrafts, setSubtaskDrafts] = useState<
    Record<number, { name: string; assignedEmployeeIds: number[] }>
  >({});
  const [pendingDelete, setPendingDelete] = useState<
    { kind: 'task' } | { kind: 'subtask'; subtask: ProjectSubtask } | null
  >(null);
  const [fieldErrors, setFieldErrors] = useState<TaskFormFieldErrors>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<Editor>(null);
  const [completeMove, setCompleteMove] = useState<ProjectColumn | null>(null);
  const [showTimeLog, setShowTimeLog] = useState(false);
  const [draggingSubtaskId, setDraggingSubtaskId] = useState<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const subtaskBoxes = useRef(new Map<number, { y: number; height: number }>());
  // Row nodes, so positions can be re-read when a drag starts: positions
  // taken at layout time are stale once the list has scrolled.
  const subtaskNodes = useRef(new Map<number, SubtaskRowNode>());
  // Web drags end on the window's pointerup, wherever the pointer is released.
  const releaseWebDrag = useRef<(() => void) | null>(null);

  const themed = useMemo(
    () => ({
      card: {
        backgroundColor: palette.surface,
        borderColor: palette.border,
        borderWidth: 1,
        borderRadius: 16,
        minWidth: CARD_MIN_WIDTH,
        maxWidth: CARD_MAX_WIDTH,
      },
      title: {
        ...typography.screenTitle,
        color: palette.textPrimary,
        fontSize: 24,
        lineHeight: 32,
      },
      meta: { ...typography.caption, color: palette.textSecondary },
      propertyLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      propertyValue: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      propertyMuted: { ...typography.body, color: palette.textMuted, fontWeight: '600' as const },
      section: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      error: { ...typography.caption, color: palette.error },
      hint: { ...typography.caption, color: palette.textSecondary },
      subtaskName: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      railLabel: { ...typography.caption, color: palette.textSecondary, fontWeight: '600' as const },
      menu: {
        borderWidth: 1,
        borderColor: palette.border,
        backgroundColor: palette.surface,
        borderRadius: 10,
        paddingVertical: spacing.xs,
        minWidth: 180,
      },
    }),
    [palette],
  );

  function applyTask(next: ProjectTask, notify = false) {
    setTask(next);
    setTaskName(next.taskName);
    setAssignedEmployeeIds(next.assignees.map(item => item.employeeId));
    setDueDate(next.dueDate ?? '');
    setTimeEstimate(formatDurationInput(next.timeEstimateMinutes));
    setTimeSpent(formatDurationInput(next.timeSpentMinutes) || '00:00');
    setDescription(next.description ?? '');
    setSubtaskDrafts(
      Object.fromEntries(
        next.subtasks
          .filter(item => item.subtaskId != null)
          .map(item => [
            item.subtaskId as number,
            {
              name: item.subtaskName,
              assignedEmployeeIds: item.assignees.map(assignee => assignee.employeeId),
            },
          ]),
      ),
    );
    if (notify) onTaskChanged?.();
  }

  const loadTask = useCallback(async () => {
    try {
      applyTask(await getProjectTask(projectId, taskId));
      setError('');
    } catch (caught) {
      if (!initialTask) {
        setError(projectErrorMessage(caught, 'Unable to load the task.'));
      }
    }
  }, [initialTask, projectId, taskId]);

  useEffect(() => {
    void loadTask();
  }, [loadTask]);

  useEffect(() => {
    if (timeLogVersion) void loadTask();
    // Only a newly logged focus session should trigger this reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLogVersion]);

  const column = columns.find(item => item.projectColumnId === task?.projectColumnId);
  const requiresDetails = column?.requiresTaskDetails === true;
  const timerForThisTask = activeTimer?.taskId === taskId ? activeTimer : null;

  async function persist(overrides: {
    taskName?: string;
    assignedEmployeeIds?: number[];
    dueDate?: string;
    timeEstimate?: string;
    timeSpent?: string;
    description?: string;
  } = {}) {
    if (!canEdit || busy || !column) return false;
    const nextName = overrides.taskName ?? taskName;
    const nextMembers = overrides.assignedEmployeeIds ?? assignedEmployeeIds;
    const nextDue = overrides.dueDate ?? dueDate;
    const nextEstimate = overrides.timeEstimate ?? timeEstimate;
    const nextSpent = overrides.timeSpent ?? timeSpent;
    const nextDescription = overrides.description ?? description;
    const result = validateCreateTaskForm({
      taskName: nextName,
      assignedEmployeeIds: nextMembers,
      dueDate: nextDue,
      timeEstimate: nextEstimate,
      timeSpent: nextSpent,
      description: nextDescription,
      descriptionOpen: true,
      subtasks: [],
      requiresTaskDetails: requiresDetails,
      columnName: column.columnName,
    });
    setFieldErrors(result.fieldErrors);
    const payload = result.payload;
    if (!payload) return false;
    const completedDate = task?.completedDate ?? null;
    setBusy(true);
    setError('');
    try {
      const next = await runOperation('update', () => updateProjectTask(projectId, taskId, {
        taskName: payload.taskName,
        assignedEmployeeIds: payload.assignedEmployeeIds ?? [],
        dueDate: payload.dueDate,
        completedDate,
        timeEstimate: payload.timeEstimate,
        timeSpent: payload.timeSpent,
        description: payload.description,
      }));
      applyTask(next, true);
      setEditor(null);
      return true;
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to save the task.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function subtaskNameError(points: string[]): string | null {
    if (!points.length) return 'Subtask name cannot be empty.';
    if (points.some(point => point.length > SUBTASK_NAME_MAX_LENGTH)) {
      return `Subtask must not exceed ${SUBTASK_NAME_MAX_LENGTH} characters.`;
    }
    return null;
  }

  /** Creates one subtask per point, in order; returns the task after the last. */
  async function createSubtasks(points: string[], assignedEmployeeIds: number[]) {
    let next: ProjectTask | null = null;
    for (const point of points) {
      next = await createProjectSubtask(projectId, taskId, {
        subtaskName: point,
        assignedEmployeeIds,
      });
    }
    return next;
  }

  async function addSubtask() {
    if (!canEdit || busy) return;
    // Pasted or typed lists become one subtask per point.
    const typed = splitSubtaskPoints(newSubtaskName);
    const invalid = subtaskNameError(typed);
    const points = newSubtaskPoints(
      typed,
      task?.subtasks.map(item => item.subtaskName) ?? [],
    );
    const message =
      invalid ?? (points.length ? null : 'This subtask already exists.');
    if (message) {
      setFieldErrors(current => ({ ...current, subtasks: message }));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next = await runOperation('add', () =>
        createSubtasks(points, newSubtaskMemberIds),
      );
      if (next) applyTask(next, true);
      setNewSubtaskName('');
      setNewSubtaskMemberIds([]);
      setFieldErrors(current => ({ ...current, subtasks: undefined }));
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to add the subtask.'));
      // Earlier points may already be saved; show what the API now has.
      void loadTask();
    } finally {
      setBusy(false);
    }
  }

  async function saveSubtask(
    subtask: ProjectSubtask,
    changes: { isCompleted?: boolean; assignedEmployeeIds?: number[] } = {},
  ) {
    if (!canEdit || busy || subtask.subtaskId == null) return;
    const draft = subtaskDrafts[subtask.subtaskId];
    const raw = draft?.name ?? subtask.subtaskName;
    const points = splitSubtaskPoints(raw);
    // A single point is saved as typed; a list keeps the first point here and
    // adds the rest as new subtasks.
    const name = isSubtaskList(raw) ? points[0] ?? '' : raw.trim();
    const invalid = subtaskNameError(name ? [name, ...points.slice(1)] : []);
    if (invalid) {
      setFieldErrors(current => ({ ...current, subtasks: invalid }));
      return;
    }
    const assignedEmployeeIds =
      changes.assignedEmployeeIds ??
      draft?.assignedEmployeeIds ??
      subtask.assignees.map(item => item.employeeId);
    const isCompleted = changes.isCompleted ?? subtask.isCompleted;
    const extra = newSubtaskPoints(
      points.slice(1),
      (task?.subtasks ?? [])
        .filter(item => item.subtaskId !== subtask.subtaskId)
        .map(item => item.subtaskName)
        .concat(name),
    );
    if (
      name === subtask.subtaskName &&
      !extra.length &&
      changes.isCompleted === undefined &&
      changes.assignedEmployeeIds === undefined
    ) {
      return;
    }
    const subtaskId = subtask.subtaskId;
    setBusy(true);
    setError('');
    try {
      const next = await runOperation('update', async () => {
        const updated = await updateProjectSubtask(projectId, subtaskId, {
          subtaskName: name,
          assignedEmployeeIds,
          isCompleted,
        });
        return (await createSubtasks(extra, assignedEmployeeIds)) ?? updated;
      });
      applyTask(next, true);
      setFieldErrors(current => ({ ...current, subtasks: undefined }));
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to save the subtask.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    const pending = pendingDelete;
    if (!pending || !canEdit) return;
    setBusy(true);
    try {
      if (pending.kind === 'task') {
        await runOperation('delete', () => deleteProjectTask(projectId, taskId), {
          failureDetail: caught =>
            projectErrorMessage(caught, 'The task could not be deleted.'),
        });
        setPendingDelete(null);
        onDeleted?.(taskId);
        return;
      }
      const subtaskId = pending.subtask.subtaskId;
      if (subtaskId == null) return;
      applyTask(
        await runOperation('delete', () => deleteProjectSubtask(projectId, subtaskId), {
          failureDetail: caught =>
            projectErrorMessage(caught, 'The subtask could not be deleted.'),
        }),
        true,
      );
    } catch {
      // runOperation already showed the "Delete failed" popup.
    } finally {
      setBusy(false);
      setPendingDelete(current => (current === pending ? null : current));
    }
  }

  function endWebDrag() {
    releaseWebDrag.current?.();
    releaseWebDrag.current = null;
  }

  useEffect(() => endWebDrag, []);

  /**
   * Starts reordering from a subtask's drag handle. Both scroll views are
   * locked while dragging, so every way a press can end (release, cancel,
   * window blur) also ends the drag; otherwise scrolling would stay locked.
   */
  function measureSubtaskBoxes() {
    subtaskNodes.current.forEach((node, id) => {
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        subtaskBoxes.current.set(id, { y: rect.top, height: rect.height });
        return;
      }
      node.measureInWindow?.((_x, y, _w, height) => {
        subtaskBoxes.current.set(id, { y, height });
      });
    });
  }

  function startDrag(id: number, pageY: number) {
    if (!canEdit) return;
    // Scrolling is locked during the drag, so these stay valid until the drop.
    measureSubtaskBoxes();
    setDraggingSubtaskId(id);
    setDragY(pageY);
    const browser = globalThis as unknown as Partial<WebPointerTarget>;
    if (Platform.OS !== 'web' || !browser.addEventListener || !browser.removeEventListener) {
      return;
    }
    const target = browser as WebPointerTarget;
    endWebDrag();
    const move = (event: { clientY: number }) => setDragY(event.clientY);
    const up = (event: { clientY: number }) => {
      endWebDrag();
      dropSubtask(event.clientY, id);
    };
    const cancel = () => {
      endWebDrag();
      setDraggingSubtaskId(null);
    };
    const listeners = [
      ['pointermove', move],
      ['pointerup', up],
      ['pointercancel', cancel],
      ['blur', cancel],
    ] as const;
    listeners.forEach(([type, listener]) => target.addEventListener(type, listener));
    releaseWebDrag.current = () =>
      listeners.forEach(([type, listener]) => target.removeEventListener(type, listener));
  }

  function dropSubtask(pageY: number, sourceId: number | null = draggingSubtaskId) {
    setDraggingSubtaskId(null);
    if (!canEdit || sourceId == null || !task) return;
    const ordered = [...task.subtasks]
      .filter(item => item.subtaskId != null)
      .sort((left, right) => left.displayOrder - right.displayOrder);
    const ids = ordered.map(item => item.subtaskId as number);
    const without = ids.filter(id => id !== sourceId);
    let insertAt = without.length;
    for (let index = 0; index < without.length; index += 1) {
      const box = subtaskBoxes.current.get(without[index]);
      if (!box) continue;
      if (pageY < box.y + box.height / 2) {
        insertAt = index;
        break;
      }
    }
    without.splice(insertAt, 0, sourceId);
    if (without.join(',') === ids.join(',')) return;
    void (async () => {
      setBusy(true);
      try {
        applyTask(
          await runOperation(
            'update',
            () => reorderProjectSubtasks(projectId, taskId, without),
            {
              failureDetail: caught =>
                projectErrorMessage(caught, 'The subtasks could not be reordered.'),
            },
          ),
          true,
        );
      } catch {
        // runOperation already showed the "Update failed" popup.
      } finally {
        setBusy(false);
      }
    })();
  }

  async function moveTo(target: ProjectColumn) {
    if (!canEdit || busy || !task || !target.projectColumnId) return;
    const plan = planTaskMove({
      currentColumnId: task.projectColumnId,
      column: {
        ...target,
        taskCount: target.taskCount ?? 0,
        taskLimit: target.taskLimit,
      },
      assignedEmployeeId: task.assignedEmployeeId,
      assignedEmployeeIds: assignedEmployeeIds,
      dueDate,
      timeEstimate,
    });
    if (plan.kind === 'noop') {
      setEditor(null);
      return;
    }
    if (plan.kind === 'blocked') {
      Alert.alert(plan.title, plan.message);
      return;
    }
    if (plan.kind === 'promptComplete') {
      setEditor(null);
      setCompleteMove(target);
      return;
    }
    setBusy(true);
    try {
      const projectColumnId = target.projectColumnId;
      applyTask(
        await runOperation(
          'update',
          () => moveProjectTask(projectId, taskId, projectColumnId, { completedDate: null }),
          {
            failureDetail: caught =>
              projectErrorMessage(caught, 'The task could not be moved.'),
          },
        ),
        true,
      );
      setEditor(null);
    } catch {
      // runOperation already showed the "Update failed" popup.
    } finally {
      setBusy(false);
    }
  }

  if (!task) {
    return (
      <View style={[themed.card, styles.emptyCard]}>
        <Text style={themed.hint}>{error || 'Loading task…'}</Text>
        <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose}>
          <Text style={themed.meta}>Close</Text>
        </Pressable>
      </View>
    );
  }

  const spentLabel = formatDurationCompact(task.timeSpentMinutes) || '0m';
  const estimateLabel = formatDurationCompact(task.timeEstimateMinutes) || 'Add';
  const dueLabel = task.dueDate ? formatCalendarDate(task.dueDate) : 'Add';
  const memberLabel =
    task.assignees.map(item => item.employeeName).join(', ') ||
    task.assignedEmployeeName ||
    'Add';
  const completedCount = task.subtasks.filter(item => item.isCompleted).length;
  const newSubtaskPointCount = newSubtaskPoints(
    splitSubtaskPoints(newSubtaskName),
    task.subtasks.map(item => item.subtaskName),
  ).length;

  return (
    <ScrollView
      horizontal
      testID="task-details-frame"
      style={styles.frame}
      contentContainerStyle={styles.frameContent}
      keyboardShouldPersistTaps="handled"
      scrollEnabled={draggingSubtaskId == null}
      onLayout={event => setFrameWidth(event.nativeEvent.layout.width)}
    >
      <View style={styles.shell}>
        <View
          testID="task-details-card"
          style={[themed.card, styles.card, { width: cardWidth }]}
          {...(Platform.OS === 'web'
            ? {
                onClick: (event: { stopPropagation?: () => void }) =>
                  event.stopPropagation?.(),
              }
            : {})}
        >
          <View style={styles.cardHeader}>
            <View style={styles.flex}>
              <TextInput
                accessibilityLabel="Task Name"
                value={taskName}
                onChangeText={setTaskName}
                {...finishEditingProps(() => {
                  if (taskName.trim() !== task.taskName) void persist();
                })}
                editable={canEdit}
                placeholder="Task name"
                placeholderTextColor={palette.textMuted}
                style={themed.title}
              />
              <Text accessibilityLabel={`Column: ${column?.columnName ?? 'Task details'}`} style={themed.meta}>
                {column?.columnName ?? 'Task details'}
              </Text>
              {task.createdByName ? (
                <Text style={themed.meta}>Added by {task.createdByName}</Text>
              ) : null}
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={onClose}
              hitSlop={8}
            >
              <Text style={[themed.meta, styles.close]}>X</Text>
            </Pressable>
          </View>

          <ScrollView
            testID="task-details-scroll"
            style={styles.bodyScroll}
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
            scrollEnabled={draggingSubtaskId == null}
            // Native drags follow the touch; web drags use window pointer events.
            onTouchMove={
              Platform.OS === 'web'
                ? undefined
                : event => {
                    if (draggingSubtaskId == null) return;
                    setDragY(event.nativeEvent.pageY);
                  }
            }
            onTouchEnd={
              Platform.OS === 'web'
                ? undefined
                : event => {
                    if (draggingSubtaskId == null) return;
                    dropSubtask(event.nativeEvent.pageY);
                  }
            }
            onTouchCancel={
              Platform.OS === 'web' ? undefined : () => setDraggingSubtaskId(null)
            }
          >
            {showTimeLog ? (
              <TaskTimeLogView
                key={timeLogVersion}
                projectId={projectId}
                taskId={taskId}
                taskName={task.taskName}
                canEdit={canEdit}
                existingMinutes={task.timeSpentMinutes}
                employeeId={task.assignedEmployeeId ?? task.assignees[0]?.employeeId}
                employeeName={
                  task.assignedEmployeeName ?? task.assignees[0]?.employeeName
                }
                members={members}
                assignees={task.assignees}
                onBack={() => setShowTimeLog(false)}
                onChanged={minutes => {
                  setTask(current =>
                    current ? { ...current, timeSpentMinutes: minutes } : current,
                  );
                  setTimeSpent(formatDurationInput(minutes) || '00:00');
                  onTaskChanged?.();
                }}
                onClearRecordedTime={() => persist({ timeSpent: '00:00' })}
              />
            ) : (
            <>
            <View style={styles.properties}>
              <PropertyChip
                label="Members"
                value={memberLabel}
                muted={!task.assignees.length}
                onPress={() => setEditor(current => (current === 'members' ? null : 'members'))}
              />
              <PropertyChip
                label="Due date"
                value={dueLabel}
                muted={!task.dueDate}
                danger={Boolean(
                  task.dueDate &&
                    !column?.isCompletedColumn &&
                    (task.dueDate.slice(0, 10) < todayBusinessCalendarDate()),
                )}
                onPress={() => setEditor(current => (current === 'due' ? null : 'due'))}
              />
              <PropertyChip
                label="Time spent"
                value={spentLabel}
                underline
                onPress={() => setShowTimeLog(true)}
              />
              <DurationField
                label="Time estimate"
                value={timeEstimate}
                error={fieldErrors.timeEstimate}
                canEdit={canEdit}
                busy={busy}
                displayValue={estimateLabel}
                onChange={setTimeEstimate}
                onSave={() => {
                  const current = formatDurationInput(task.timeEstimateMinutes);
                  if (timeEstimate.trim() === current) return;
                  void persist({ timeEstimate });
                }}
              />
            </View>

            {editor === 'members' ? (
              <View style={styles.editor}>
                <ProjectMemberPicker
                  label={requiresDetails ? 'Members *' : 'Members'}
                  members={members}
                  selectedIds={assignedEmployeeIds}
                  onChange={setAssignedEmployeeIds}
                  error={fieldErrors.member}
                />
                {canEdit ? (
                  <AppButton title="Save task" onPress={() => void persist()} loading={busy} />
                ) : null}
              </View>
            ) : null}
            {fieldErrors.dueDate && editor !== 'due' ? (
              <Text style={themed.error}>{fieldErrors.dueDate}</Text>
            ) : null}

            <AutoGrowTextInput
              accessibilityLabel="Description"
              value={description}
              onChangeText={setDescription}
              {...finishEditingProps(() => {
                if ((description || '') !== (task.description || '')) void persist();
              })}
              placeholder="Add a description..."
              placeholderTextColor={palette.textMuted}
              editable={canEdit}
              minHeight={72}
              style={[
                styles.description,
                {
                  borderColor: palette.border,
                  color: palette.textPrimary,
                },
              ]}
            />

            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}

            <View style={styles.sectionHeader}>
              <Text style={themed.section}>
                Subtasks {completedCount} / {task.subtasks.length}
              </Text>
            </View>
            {canEdit && task.subtasks.length > 1 ? (
              <Text style={themed.hint}>Hold ⠿ and drag to reorder a subtask.</Text>
            ) : null}

            {task.subtasks.map(subtask => {
              const id = subtask.subtaskId;
              if (id == null) return null;
              const draft = subtaskDrafts[id] ?? {
                name: subtask.subtaskName,
                assignedEmployeeIds: subtask.assignees.map(item => item.employeeId),
              };
              return (
                <View
                  key={id}
                  testID={`subtask-row-${id}`}
                  style={styles.subtaskRow}
                  onLayout={event => {
                    // react-native-web layout events carry the DOM node on
                    // nativeEvent.target and have no currentTarget.
                    const node = (
                      Platform.OS === 'web'
                        ? (event.nativeEvent as { target?: unknown }).target
                        : event.currentTarget
                    ) as SubtaskRowNode | undefined;
                    if (node) subtaskNodes.current.set(id, node);
                  }}
                >
                  {canEdit ? (
                    <Pressable
                      testID={`subtask-drag-handle-${id}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Reorder ${subtask.subtaskName}`}
                      accessibilityHint="Hold and drag to move this subtask"
                      hitSlop={6}
                      delayLongPress={200}
                      // Native: hold, then drag. Web: press and drag right away.
                      onLongPress={
                        Platform.OS === 'web'
                          ? undefined
                          : event => startDrag(id, event.nativeEvent.pageY)
                      }
                      onPressIn={
                        Platform.OS === 'web'
                          ? event => startDrag(id, event.nativeEvent.pageY)
                          : undefined
                      }
                      style={[
                        styles.dragHandle,
                        Platform.OS === 'web' ? ({ cursor: 'grab' } as object) : null,
                      ]}
                    >
                      <Text style={[styles.dragHandleIcon, { color: palette.textMuted }]}>⠿</Text>
                    </Pressable>
                  ) : null}
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: subtask.isCompleted }}
                    accessibilityLabel={`Mark ${subtask.subtaskName} ${
                      subtask.isCompleted ? 'incomplete' : 'complete'
                    }`}
                    onPress={() =>
                      void saveSubtask(subtask, { isCompleted: !subtask.isCompleted })
                    }
                    style={[
                      styles.checkbox,
                      {
                        borderColor: palette.border,
                        backgroundColor: subtask.isCompleted ? palette.primary : palette.surface,
                      },
                    ]}
                  >
                    {subtask.isCompleted ? (
                      <Text style={[styles.checkMark, { color: palette.onPrimary }]}>✓</Text>
                    ) : null}
                  </Pressable>
                  {/* Plain wrapper: holding or selecting text must not start a drag. */}
                  <View style={styles.flex}>
                    <AutoGrowTextInput
                      accessibilityLabel="Subtask name"
                      value={draft.name}
                      onChangeText={value =>
                        setSubtaskDrafts(current => ({
                          ...current,
                          [id]: { ...draft, name: value },
                        }))
                      }
                      {...finishEditingProps(() => void saveSubtask(subtask))}
                      editable={canEdit}
                      minHeight={32}
                      style={[
                        themed.subtaskName,
                        styles.subtaskInput,
                        subtask.isCompleted
                          ? { textDecorationLine: 'line-through', color: palette.textMuted }
                          : null,
                      ]}
                    />
                  </View>
                  <View style={{ width: memberFieldWidth }}>
                    <ProjectMemberPicker
                      label="Subtask members"
                      showLabel={false}
                      members={members}
                      selectedIds={draft.assignedEmployeeIds}
                      onChange={ids => {
                        setSubtaskDrafts(current => ({
                          ...current,
                          [id]: { ...draft, assignedEmployeeIds: ids },
                        }));
                        if (canEdit) void saveSubtask(subtask, { assignedEmployeeIds: ids });
                      }}
                      accessibilityLabel={`Assign members to ${subtask.subtaskName}`}
                    />
                  </View>
                  {canEdit ? (
                    <ConfigurationDeleteButton
                      entityLabel={`subtask ${subtask.subtaskName}`}
                      placement="inline"
                      disabled={busy}
                      onPress={() => setPendingDelete({ kind: 'subtask', subtask })}
                    />
                  ) : null}
                </View>
              );
            })}

            {canEdit ? (
              <View style={styles.addSubtask}>
                <View style={styles.subtaskRow}>
                  <AutoGrowTextInput
                    accessibilityLabel="New subtask"
                    value={newSubtaskName}
                    onChangeText={value => {
                      setNewSubtaskName(value);
                      if (fieldErrors.subtasks) {
                        setFieldErrors(current => ({ ...current, subtasks: undefined }));
                      }
                    }}
                    placeholder="Add subtask... (paste a list to add several)"
                    placeholderTextColor={palette.textMuted}
                    minHeight={40}
                    style={[
                      styles.addSubtaskInput,
                      { color: palette.textPrimary, borderColor: palette.border },
                    ]}
                  />
                  <View style={{ width: memberFieldWidth }}>
                    <ProjectMemberPicker
                      label="Subtask members"
                      showLabel={false}
                      members={members}
                      selectedIds={newSubtaskMemberIds}
                      onChange={setNewSubtaskMemberIds}
                      accessibilityLabel="Assign members to new subtask"
                    />
                  </View>
                </View>
                {newSubtaskPointCount > 1 ? (
                  <Text style={themed.hint}>
                    {newSubtaskPointCount} subtasks will be added.
                  </Text>
                ) : null}
                <AppButton
                  title={newSubtaskPointCount > 1 ? 'Add subtasks' : 'Add subtask'}
                  onPress={() => void addSubtask()}
                  loading={busy}
                />
                {fieldErrors.subtasks ? (
                  <Text style={themed.error}>{fieldErrors.subtasks}</Text>
                ) : null}
              </View>
            ) : null}
            </>
            )}
          </ScrollView>
        </View>

        <View style={styles.rail}>
          <RailButton
            label="Move"
            icon="→"
            active={editor === 'move'}
            onPress={() => setEditor(current => (current === 'move' ? null : 'move'))}
          />
          <RailButton
            label="Timer"
            icon="⏱"
            active={editor === 'timer' || Boolean(timerForThisTask)}
            onPress={() => setEditor(current => (current === 'timer' ? null : 'timer'))}
          />
          {canEdit ? (
            <RailButton
              label="Delete"
              icon="✕"
              danger
              onPress={() => {
                setEditor(null);
                setPendingDelete({ kind: 'task' });
              }}
            />
          ) : null}
          {editor === 'move' ? (
            <View style={[themed.menu, styles.railMenu]}>
              {columns
                .filter(item => item.projectColumnId != null)
                .map(item => (
                  <Pressable
                    key={String(item.projectColumnId)}
                    accessibilityRole="button"
                    accessibilityLabel={`Move to ${item.columnName}`}
                    onPress={() => void moveTo(item)}
                    style={styles.menuItem}
                  >
                    <Text style={themed.subtaskName}>{item.columnName}</Text>
                  </Pressable>
                ))}
            </View>
          ) : null}
          {editor === 'timer' ? (
            <View style={[themed.menu, styles.railMenu]}>
              {(Object.keys(FOCUS_TIMER_PRESETS) as FocusTimerMode[]).map(mode => (
                <Pressable
                  key={mode}
                  accessibilityRole="button"
                  accessibilityLabel={`Start ${FOCUS_TIMER_PRESETS[mode].label} ${FOCUS_TIMER_PRESETS[mode].ratioLabel} for ${task.taskName}`}
                  onPress={() => {
                    onStartTimer(mode);
                    setEditor(null);
                  }}
                  style={styles.menuItem}
                >
                  <View style={styles.timerMenuRow}>
                    <Text style={styles.timerMenuIcon}>
                      {FOCUS_TIMER_PRESETS[mode].icon}
                    </Text>
                    <Text style={themed.subtaskName}>
                      {FOCUS_TIMER_PRESETS[mode].label}{' '}
                      {FOCUS_TIMER_PRESETS[mode].ratioLabel}
                    </Text>
                  </View>
                </Pressable>
              ))}
              {timerForThisTask ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Stop timer for ${task.taskName}`}
                  onPress={() => {
                    onStopTimer();
                    setEditor(null);
                  }}
                  style={styles.menuItem}
                >
                  <Text style={themed.hint}>
                    ✕ Stop {timerForThisTask.remainingLabel}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </View>

        <CalendarDatePicker
          open={editor === 'due'}
          value={dueDate}
          onClose={() => setEditor(null)}
          onConfirm={value => {
            setDueDate(value);
            void persist({ dueDate: value });
          }}
        />
        <CompleteTaskModal
          visible={completeMove != null}
          projectId={projectId}
          column={
            completeMove
              ? {
                  ...completeMove,
                  taskCount: completeMove.taskCount ?? 0,
                  tasks: [],
                }
              : null
          }
          members={members}
          task={task}
          onClose={() => setCompleteMove(null)}
          onCompleted={() => {
            setCompleteMove(null);
            void loadTask();
            onTaskChanged?.();
          }}
        />
        <ConfirmDialog
          visible={pendingDelete != null}
          title="Confirm to delete?"
          destructive
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
        {draggingSubtaskId != null ? (
          <View pointerEvents="none" style={[styles.dragGhost, { top: dragY - 18 }]}>
            <Text style={[themed.subtaskName, { color: palette.primary }]}>
              {task.subtasks.find(item => item.subtaskId === draggingSubtaskId)?.subtaskName}
            </Text>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}

function PropertyChip({
  label,
  value,
  muted,
  danger,
  underline,
  onPress,
}: {
  label: string;
  value: string;
  muted?: boolean;
  danger?: boolean;
  underline?: boolean;
  onPress: () => void;
}) {
  const palette = useAppColors();
  function handlePress(event?: { stopPropagation?: () => void }) {
    event?.stopPropagation?.();
    onPress();
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={event => handlePress(event)}
      {...(Platform.OS === 'web'
        ? {
            onClick: (event: { stopPropagation?: () => void }) => handlePress(event),
          }
        : {})}
      style={[
        styles.property,
        Platform.OS === 'web' ? { cursor: 'pointer' as const } : null,
      ]}
    >
      <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>{label}</Text>
      <Text
        style={[
          styles.propertyValue,
          {
            color: danger
              ? palette.error
              : underline
                ? palette.primary
                : muted
                  ? palette.textMuted
                  : palette.textPrimary,
            textDecorationLine: underline ? 'underline' : 'none',
          },
        ]}
      >
        {value}
      </Text>
    </Pressable>
  );
}

function DurationField({
  label,
  value,
  displayValue,
  error,
  canEdit,
  busy,
  onChange,
  onSave,
}: {
  label: string;
  value: string;
  displayValue: string;
  error?: string;
  canEdit: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  const palette = useAppColors();
  return (
    <View style={styles.durationField}>
      <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>{label}</Text>
      {canEdit ? (
        <TextInput
          accessibilityLabel={label}
          value={value}
          onChangeText={onChange}
          onEndEditing={onSave}
          placeholder="00:00"
          placeholderTextColor={palette.textMuted}
          autoCapitalize="none"
          keyboardType="numbers-and-punctuation"
          style={[
            styles.durationInput,
            {
              borderColor: error ? palette.error : palette.border,
              color: palette.textPrimary,
            },
          ]}
        />
      ) : (
        <Text
          accessibilityLabel={label}
          style={[styles.propertyValue, { color: palette.textPrimary }]}
        >
          {displayValue}
        </Text>
      )}
      {error ? (
        <Text accessibilityRole="alert" style={[styles.propertyLabel, { color: palette.error }]}>
          {error}
        </Text>
      ) : (
        <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>hh:mm</Text>
      )}
      {canEdit ? (
        <AppButton
          title="Save"
          accessibilityLabel={`Save ${label.toLowerCase()}`}
          onPress={onSave}
          loading={busy}
        />
      ) : null}
    </View>
  );
}

function RailButton({
  label,
  icon,
  active,
  danger,
  onPress,
}: {
  label: string;
  icon: string;
  active?: boolean;
  danger?: boolean;
  onPress: () => void;
}) {
  const palette = useAppColors();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={styles.railItem}
    >
      <View
        style={[
          styles.railCircle,
          {
            backgroundColor: palette.surface,
            borderColor: active ? palette.primary : danger ? palette.error : palette.border,
          },
        ]}
      >
        <Text style={{ color: danger ? palette.error : palette.textPrimary, fontWeight: '700' }}>
          {icon}
        </Text>
      </View>
      <Text style={[styles.railLabel, { color: palette.textSecondary }]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Content-height, but shrinks to the space its host gives it so the body
  // ScrollView below gets a bounded height and can scroll vertically.
  frame: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: CARD_MAX_WIDTH + spacing.md + RAIL_WIDTH,
    alignSelf: 'center',
    zIndex: 2,
  },
  frameContent: { flexGrow: 1 },
  shell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  // Stretch to the frame height (the rail stays top-aligned).
  card: { alignSelf: 'stretch', minHeight: 0 },
  bodyScroll: { flexGrow: 0, flexShrink: 1, minHeight: 0 },
  emptyCard: {
    padding: spacing.xl,
    gap: spacing.md,
    minWidth: CARD_MIN_WIDTH,
    alignSelf: 'center',
  },
  flex: { flex: 1 },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    gap: spacing.md,
  },
  close: { fontWeight: '700', fontSize: 18 },
  body: {
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xxl,
    paddingTop: spacing.md,
    gap: spacing.md,
  },
  properties: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  property: {
    minWidth: 110,
    gap: 2,
  },
  propertyLabel: { ...typography.caption, fontWeight: '600' },
  propertyValue: { ...typography.body, fontWeight: '700' },
  editor: { gap: spacing.sm },
  durationField: {
    minWidth: 140,
    flexGrow: 1,
    gap: 4,
  },
  durationInput: {
    ...typography.input,
    borderWidth: 1,
    borderRadius: 10,
    minHeight: 44,
    paddingHorizontal: spacing.md,
  },
  description: {
    ...typography.input,
    minHeight: 72,
    borderBottomWidth: 1,
    paddingVertical: spacing.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  // Text, member and delete share one row; long text wraps and grows the row.
  subtaskRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    position: 'relative',
  },
  subtaskInput: { paddingVertical: 4, minWidth: 0 },
  checkbox: {
    width: 22,
    height: 22,
    marginTop: 5,
    borderWidth: 1,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkMark: { fontSize: 12, fontWeight: '700' },
  dragHandle: {
    width: 20,
    minHeight: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dragHandleIcon: { fontSize: 16, lineHeight: 20 },
  addSubtask: { gap: spacing.sm, marginTop: spacing.sm },
  addSubtaskInput: {
    ...typography.input,
    flex: 1,
    minWidth: 0,
    borderBottomWidth: 1,
    paddingVertical: spacing.sm,
  },
  rail: { paddingTop: spacing.lg, gap: spacing.md, width: RAIL_WIDTH, alignItems: 'center' },
  railItem: { alignItems: 'center', gap: 4 },
  railCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  railLabel: { ...typography.caption, fontWeight: '600' },
  // Opens over the card; the horizontal frame clips anything right of the rail.
  railMenu: { position: 'absolute', right: RAIL_WIDTH + 4, top: 8, zIndex: 5 },
  menuItem: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  timerMenuRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  timerMenuIcon: {
    fontSize: 16,
    lineHeight: 22,
    ...Platform.select({
      web: {
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      },
      default: {},
    }),
  },
  dragGhost: {
    position: 'absolute',
    left: spacing.xl,
    right: 90,
  },
});
